// ---------------------------------------------------------------------------
// G.19: Breakout Rooms — Main Orchestrator
// ---------------------------------------------------------------------------
// Host: setup -> draft confirmation -> active monitoring -> ended (reset).
// Participant: self-select room list OR assignment notification.
// ---------------------------------------------------------------------------

import { useState, useEffect, useCallback, useRef } from "react";
import { useParams } from "react-router";
import { apiFetch } from "@/lib/api-client";
import type { BreakoutStatus, BreakoutStatusResponse } from "./breakout-types";
import { POLL_INTERVAL_MS } from "./breakout-types";
import { BreakoutSetupPanel } from "./breakout-setup-panel";
import { BreakoutActiveView } from "./breakout-active-view";
import { ParticipantSelfSelectView, ParticipantAssignedView } from "./breakout-participant-views";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface BreakoutRoomsProps {
  webinarId: string;
  isHost: boolean;
  currentUserId?: string;
}

export function BreakoutRoomsManagementPage() {
  const { webinarId } = useParams();

  if (!webinarId) {
    return (
      <div role="alert" className="mx-auto max-w-3xl p-4 text-sm text-[var(--color-destructive)]">
        Webinar id is required to manage breakout timers.
      </div>
    );
  }

  return <BreakoutRooms webinarId={webinarId} isHost />;
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export default function BreakoutRooms({ webinarId, isHost, currentUserId: _currentUserId }: BreakoutRoomsProps) {
  const [breakoutId, setBreakoutId] = useState<string | null>(null);
  const [breakoutStatus, setBreakoutStatus] = useState<BreakoutStatus | null>(null);
  const [statusData, setStatusData] = useState<BreakoutStatusResponse["data"] | null>(null);
  const [isStarting, setIsStarting] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchStatus = useCallback(async () => {
    if (!breakoutId) return;
    try {
      const res = await apiFetch<BreakoutStatusResponse>(
        `/v1/webinars/${webinarId}/breakout/${breakoutId}/status`,
      );
      setStatusData(res.data);
      setBreakoutStatus(res.data.status);
    } catch {
      // Retry on next interval
    }
  }, [webinarId, breakoutId]);

  useEffect(() => {
    if (!breakoutId) return;
    fetchStatus();
    pollRef.current = setInterval(fetchStatus, POLL_INTERVAL_MS);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [breakoutId, fetchStatus]);

  const handleCreated = (id: string) => {
    setBreakoutId(id);
    setBreakoutStatus("draft");
  };

  const handleStart = async () => {
    if (!breakoutId || isStarting) return;
    setIsStarting(true);
    try {
      await apiFetch(`/v1/webinars/${webinarId}/breakout/${breakoutId}/start`, {
        method: "POST",
      });
      setBreakoutStatus("active");
      await fetchStatus();
    } catch {
      // Error displayed via apiFetch
    } finally {
      setIsStarting(false);
    }
  };

  const handleEnded = () => {
    setBreakoutStatus("ended");
    setBreakoutId(null);
    setStatusData(null);
  };

  // -----------------------------------------------------------------------
  // Host Views
  // -----------------------------------------------------------------------
  if (isHost) {
    // No breakout yet — show setup
    if (!breakoutId || breakoutStatus === "ended" || !breakoutStatus) {
      return (
        <div className="mx-auto max-w-3xl p-4">
          <BreakoutSetupPanel webinarId={webinarId} onCreated={handleCreated} />
        </div>
      );
    }

    // Draft — show "Start" confirmation
    if (breakoutStatus === "draft" && statusData) {
      return (
        <div className="mx-auto max-w-3xl space-y-4 p-4">
          <div className="rounded-lg border border-border bg-card p-5">
            <h2 className="mb-2 text-base font-semibold text-foreground">Breakout Rooms Ready</h2>
            <p className="mb-4 text-sm text-muted-foreground">
              {statusData.rooms.length} rooms configured | {statusData.assignmentMode} assignment | {statusData.timerMinutes} min timer
            </p>
            <div className="mb-4 space-y-2">
              {statusData.rooms.map((room) => (
                <div key={room.id} className="flex items-center justify-between rounded-md bg-muted px-3 py-2">
                  <span className="text-sm font-medium text-foreground">{room.name}</span>
                  {room.topic && <span className="text-xs text-muted-foreground">{room.topic}</span>}
                </div>
              ))}
            </div>
            <button
              onClick={handleStart}
              disabled={isStarting}
              className="w-full rounded-md bg-[var(--color-success)] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[var(--color-success)] disabled:opacity-50"
            >
              {isStarting ? "Starting..." : "Start Breakout Session"}
            </button>
          </div>
        </div>
      );
    }

    // Active — show monitoring dashboard
    if (breakoutStatus === "active" && breakoutId) {
      return (
        <div className="mx-auto max-w-5xl p-4">
          <BreakoutActiveView
            webinarId={webinarId}
            breakoutId={breakoutId}
            onEnded={handleEnded}
          />
        </div>
      );
    }
  }

  // -----------------------------------------------------------------------
  // Participant Views
  // -----------------------------------------------------------------------
  if (!isHost && statusData && breakoutStatus === "active") {
    if (statusData.assignmentMode === "self_select") {
      return (
        <div className="mx-auto max-w-2xl p-4">
          <ParticipantSelfSelectView
            webinarId={webinarId}
            breakoutId={statusData.id}
            rooms={statusData.rooms}
            timeRemainingSeconds={statusData.timeRemainingSeconds}
            warningMinutesBefore={statusData.warningMinutesBefore}
            broadcasts={statusData.broadcasts}
          />
        </div>
      );
    }

    return (
      <div className="mx-auto max-w-2xl p-4">
        <ParticipantAssignedView
          rooms={statusData.rooms}
          timeRemainingSeconds={statusData.timeRemainingSeconds}
          warningMinutesBefore={statusData.warningMinutesBefore}
          broadcasts={statusData.broadcasts}
        />
      </div>
    );
  }

  // No active breakout for participants
  if (!isHost) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-muted-foreground">No active breakout session.</p>
      </div>
    );
  }

  return null;
}
